import React, { useState, useMemo } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/NewButton";
import { FolderInput, Folder, Check } from 'lucide-react';
import { backendClient } from '@/lib/api-client';
import { toast } from 'react-hot-toast';
import { useFolderTree } from '@/hooks/useFolders';
import { flattenFolderTree } from '@/utils/document-utils';
import { Loading } from '../shared';

interface MoveDocumentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  documentIds: string[];
  onSuccess: () => void;
  currentFolderIds?: number[]; // Array of current folder IDs for the selected documents
  matterSlug?: string;
}

export const MoveDocumentsDialog: React.FC<MoveDocumentsDialogProps> = ({
  isOpen,
  onClose,
  documentIds,
  onSuccess,
  currentFolderIds = [],
  matterSlug: matterSlugProp,
}) => {
  const { slug: routeSlug } = useParams({ strict: false });
  const matterSlug =
    matterSlugProp
    ?? (typeof routeSlug === 'string' && routeSlug && routeSlug !== 'undefined' ? routeSlug : '');
  const [selectedFolderId, setSelectedFolderId] = useState<string>('');
  const [isMoving, setIsMoving] = useState(false);

  // Use the folder tree hook
  const { folderTree, isLoading } = useFolderTree(matterSlug);

  // Flatten folder tree for selection with visual hierarchy
  const flatFolders = useMemo(
    () => (folderTree ? flattenFolderTree(folderTree) : []),
    [folderTree]
  );

  const handleMoveDocuments = async () => {
    if (!matterSlug || !selectedFolderId || documentIds.length === 0) return;

    try {
      setIsMoving(true);
      const response = await backendClient.post(`/matter/${matterSlug}/move-documents-to-folder`, {
        file_reference_guids: documentIds,
        folder_id: parseInt(selectedFolderId)
      });

      const movedCount = response.data.moved_count || 0;

      // Check for warnings (name conflicts or permission errors)
      if (response.data.warnings) {
        // Show warning message
        toast.error(response.data.warnings.message, { duration: 6000 });

        // Only call onSuccess and show success message if some files were actually moved
        if (movedCount > 0) {
          toast.success(`Successfully moved ${movedCount} file${movedCount !== 1 ? 's' : ''}`);
          onSuccess();
        }
      } else {
        // No warnings - all files moved successfully
        toast.success(`${movedCount} file${movedCount !== 1 ? 's' : ''} moved successfully`);
        onSuccess();
      }

      onClose();
    } catch (error) {
      console.error('Failed to move documents:', error);
      toast.error('Failed to move documents to folder');
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Move Documents</DialogTitle>
          <DialogDescription>
            Move {documentIds.length} selected file{documentIds.length !== 1 ? 's' : ''} to another folder
            {currentFolderIds.length > 1 && (
              <span className="block text-sm text-muted-foreground mt-1">
                Files are currently in {currentFolderIds.length} different folders
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <label className="text-sm font-medium mb-2 block" id="destination-folder-label">Destination Folder</label>
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loading text='Loading folders' />
            </div>
          ) : (
            <Select value={selectedFolderId} onValueChange={setSelectedFolderId} aria-labelledby="destination-folder-label">
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select destination folder" />
              </SelectTrigger>
              <SelectContent position="popper">
                <SelectGroup>
                  {flatFolders.map((folder) => {
                    const folderId = folder.folder_id;
                    if (!folderId) return null;
                    const isCurrentFolder = currentFolderIds.includes(folderId);
                    return (
                      <SelectItem
                        key={`move-doc-${folderId}`}
                        value={folderId.toString()}
                        className={`group cursor-pointer transition-colors duration-150 ${
                          isCurrentFolder
                            ? 'bg-blue-50 hover:bg-blue-100'
                            : 'hover:bg-accent/50'
                        }`}
                        style={{ paddingLeft: `${folder.depth * 16 + 32}px` }}
                      >
                        <div className="flex items-center w-full justify-between">
                          <div className="flex items-center flex-1 min-w-0">
                            <Folder className={`h-4 w-4 mr-2 shrink-0 ${
                              isCurrentFolder
                                ? 'text-amber-500/50 fill-amber-400/20'
                                : 'text-amber-500/40 fill-amber-400/15'
                            }`} />
                            <span className={`truncate ${
                              isCurrentFolder
                                ? 'font-semibold text-blue-900'
                                : 'font-medium'
                            }`}>
                              {folder.displayName}
                            </span>
                          </div>
                          {isCurrentFolder && (
                            <Check className="h-4 w-4 text-blue-600 shrink-0 ml-2" />
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isMoving} text='Cancel' />
          <Button
            onClick={handleMoveDocuments}
            disabled={!selectedFolderId || isMoving}
            className="flex items-center gap-2"
            loading={isMoving}
            icon={FolderInput}
            text={`Move ${documentIds.length} file${documentIds.length !== 1 ? 's' : ''}`}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
